import { combineEpics, ofType } from 'redux-observable';
import { concatMap } from 'rxjs/operators';
import { getLocalUsagePrivateMethod, getRoamingUsagePrivateMethod } from '../../generated/api/uiApiMethods.js';
import {
  getSubscriptionLocalUsageFailed,
  getSubscriptionLocalUsageFulfilled,
  getSubscriptionRoamingUsageFailed,
  getSubscriptionRoamingUsageFulfilled,
} from '../actions/employeeSubscriptionUsageActions.js';
import { TypeKeys } from '../actions/index.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { LocalUsageResponse, RoamingUsageResponse } from '../../generated/api/models.js';
import type {
  GetSubscriptionLocalUsageAction,
  GetSubscriptionRoamingUsageAction,
} from '../actions/employeeSubscriptionUsageActions.js';
import type { ActionTypes } from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';

export const getSubscriptionLocalUsageEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE)),
    (action: GetSubscriptionLocalUsageAction) => actionToActionState(action, state$, 'authenticated')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const { subscriptionId } = actionAndState.action as GetSubscriptionLocalUsageAction;
      if (!actionAndState.state && !subscriptionId) {
        throw new Error('invalid action state to get local usage , subscriptionId is missing');
      }
      return callUiApi({
        epicDependencies,
        state$,
        method: getLocalUsagePrivateMethod(subscriptionId),
        successAction: (response: LocalUsageResponse) => getSubscriptionLocalUsageFulfilled(subscriptionId, response),
        failureAction: getSubscriptionLocalUsageFailed,
      });
    })
  );

export const getSubscriptionRoamingUsageEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE)),
    (action: GetSubscriptionRoamingUsageAction) => actionToActionState(action, state$, 'authenticated')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const { subscriptionId } = actionAndState.action as GetSubscriptionRoamingUsageAction;
      if (!actionAndState.state && !subscriptionId) {
        throw new Error('invalid action state to get roaming usage, subscriptionId is missing');
      }
      return callUiApi({
        epicDependencies,
        state$,
        method: getRoamingUsagePrivateMethod(subscriptionId),
        successAction: (response: RoamingUsageResponse) =>
          getSubscriptionRoamingUsageFulfilled(subscriptionId, response),
        failureAction: getSubscriptionRoamingUsageFailed,
      });
    })
  );

export const employeeSubscriptionUsageEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  getSubscriptionLocalUsageEpic,
  getSubscriptionRoamingUsageEpic
);
